import { HttpRequest, createHttpClient, createError, ErrorCodes, Error, Method } from '@quickplay/foundation';
import { PlatformAuthorizer } from './PlatformAuthorizer';
import { makeAuthorizedHttpCall } from './internal/AuthorizedHttpCall';
import { PlatformHeader, PlatformError, PlatformResponse } from './modal/PlatformResponse';
import { PlatformErrorCodes } from './PlatformErrorCodes';

function isServerError(header: PlatformHeader): boolean {
  return header && parseInt(header.code.toString()) !== 0;
}

function createServerError(header: PlatformHeader): Error {
  const serverErrorcode = parseInt(header.errors[0].code);
  const serverErrorDescription = header.errors[0].description;
  let contextDescription = '';
  header.errors.forEach((error: PlatformError) => {
    contextDescription += error.code + ' ';
    contextDescription += error.description + ' ';
  });
  contextDescription = serverErrorDescription.trim();
  const internalServerError = createError(serverErrorcode, serverErrorDescription, undefined, contextDescription);
  return createError(
    PlatformErrorCodes.PLATFORM_SERVICE_ERROR | ErrorCodes.ERROR_CATEGORY_MASK_SERVER,
    serverErrorDescription,
    internalServerError,
  );
}

let platformServiceLatchTime = -1;
let platformServiceLatchLocalTime = -1;

function updateServerLatchTime(epochTime: number): void {
  platformServiceLatchTime = epochTime;
  if (platformServiceLatchTime !== -1) {
    platformServiceLatchLocalTime = new Date().getTime();
  }
}

export const AuthorizationUtil = {
  async makeAuthorizedRequest<T, K>(
    request: HttpRequest<T>,
    platform: PlatformAuthorizer,
  ): Promise<PlatformResponse<K>> {
    try {
      const httpClient = createHttpClient<T, PlatformResponse<K>>();
      const result = await makeAuthorizedHttpCall<T, PlatformResponse<K>>(httpClient, platform, request);

      if (!(result && result.body)) {
        return Promise.reject(
          createError(
            PlatformErrorCodes.PLATFORM_SERVICE_MALFORMED_RESPONSE | ErrorCodes.ERROR_CATEGORY_MASK_SERVER,
            'Invalid server response',
          ),
        );
      }

      const responseBody = result.body;
      const requestMethod = request.method;
      const shouldLatchServerTime = requestMethod !== Method.GET;

      // FLPS-706: Server latching is skipped for any cache-able APIs (all GET requests)
      if (shouldLatchServerTime && responseBody.header && responseBody.header.system_time) {
        updateServerLatchTime(responseBody.header.system_time);
      }

      if (isServerError(responseBody.header)) {
        return Promise.reject(createServerError(responseBody.header));
      }
      return Promise.resolve(responseBody);
    } catch (error) {
      return Promise.reject(error);
    }
  },

  platformServiceTime(): number {
    if (platformServiceLatchTime === -1) {
      return platformServiceLatchTime;
    }
    return platformServiceLatchTime + (new Date().getTime() - platformServiceLatchLocalTime);
  },
};
